import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { dashboardService, DashboardMetrics } from '../services/firebase/DashboardService';
import { Product } from '../types/product';
import { AlertCircle, Box, LayoutGrid, Clock, Plus, Settings } from 'lucide-react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import RecentProductsList from '../components/products/RecentProductsList';

interface MetricCardProps {
  title: React.ReactNode;
  value: string | number;
  icon: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon }) => (
  <div className="bg-white rounded-lg shadow p-3 lg:h-[104px] flex flex-col">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-gray-600 font-medium text-sm">{title}</h3>
      <div className="flex-shrink-0">{icon}</div>
    </div>
    <p className="text-xl font-bold text-center mt-auto">{value}</p>
  </div>
);

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin, loading: isAdminLoading } = useIsAdmin();
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalProducts: 0,
    optimizedProducts: 0,
    optimizationRate: 0,
    shopifyOptimizationRate: 0,
    amazonOptimizationRate: 0,
    googleOptimizationRate: 0,
    recentProducts: []
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const dashboardMetrics = await dashboardService.getDashboardMetrics();
      setMetrics(dashboardMetrics);
      setError(null);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const handleRefresh = () => {
      loadData();
    };

    window.addEventListener('refreshProducts', handleRefresh);
    return () => {
      window.removeEventListener('refreshProducts', handleRefresh);
    };
  }, [loadData]);

  const handleProductSelect = (productId: string) => {
    if (location.pathname.startsWith('/lms')) {
      navigate(`/lms/products/${productId}`);
    } else {
      navigate(`/lms/products/${productId}`);
    }
  };

  const handleOpenImportDialog = () => {
    const event = new CustomEvent('openImport');
    window.dispatchEvent(event);
  };

  if (isLoading || isAdminLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link 
                to="/admin"
                className="flex items-center gap-1 py-1.5 px-3 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
              >
                <Settings className="h-3.5 w-3.5" />
                Admin Panel
              </Link>
            )}
            {location.pathname.startsWith('/lms') && (
              <Button
                onClick={handleOpenImportDialog}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 py-1.5 h-auto text-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                Import Products
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
            <MetricCard
              title="Total Products"
              value={metrics.totalProducts}
              icon={<Box className="h-6 w-6 text-blue-600" />}
            />
            <MetricCard
              title="Optimized Products"
              value={metrics.optimizedProducts}
              icon={<LayoutGrid className="h-6 w-6 text-green-600" />}
            />
            <MetricCard
              title="Overall Optimization"
              value={`${Math.round(metrics.optimizationRate)}%`}
              icon={<Clock className="h-6 w-6 text-purple-600" />}
            />

            <div className="relative col-span-3">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 lg:absolute lg:-top-8">Marketplace Optimization</h2>
              <div className="grid grid-cols-3 gap-3">
                <MetricCard
                  title="Shopify"
                  value={`${Math.round(metrics.shopifyOptimizationRate)}%`}
                  icon={<svg className="h-6 w-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0L1.5 4.5v15L12 24l10.5-4.5v-15L12 0zm0 2.25l8.25 3.75v11.25L12 21l-8.25-3.75V6L12 2.25z"/>
                  </svg>}
                />
                <MetricCard
                  title="Amazon"
                  value={`${Math.round(metrics.amazonOptimizationRate)}%`}
                  icon={<svg className="h-6 w-6 text-yellow-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.743-2.64 1.316-4.185 1.726a17.617 17.617 0 01-10.951-.577 17.12 17.12 0 01-5.43-2.85A.338.338 0 010 18.315c.054-.096.108-.178.045-.295z"/>
                  </svg>}
                />
                <MetricCard
                  title="Google"
                  value={`${Math.round(metrics.googleOptimizationRate)}%`}
                  icon={<svg className="h-6 w-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm4.595 19.964a9.97 9.97 0 01-4.595 1.118 9.97 9.97 0 01-4.595-1.118c-1.378-.651-2.594-1.576-3.583-2.725l1.511-1.511C6.026 16.09 7.059 17.12 8.297 17.888a7.477 7.477 0 003.703.994 7.477 7.477 0 003.703-.994c1.238-.768 2.271-1.798 2.964-3.26l1.511 1.511c-.989 1.149-2.205 2.074-3.583 2.725z"/>
                  </svg>}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Recently Updated Products</h2>
          </div>
          <RecentProductsList
            products={metrics.recentProducts}
            onSelectProduct={handleProductSelect}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
